import OpenSeadragon from "openseadragon";
import * as React from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";
import { cacheTile, getCachedTile } from "@/offline/tile-cache";
import type { TilesMeta } from "@/types/api";

import {
  REVEAL_INSET_RATIO,
  imageToNormalized,
  normalizedToImage,
  paddedBounds,
  tileUrlFor,
  type NormalizedBounds,
  type XY,
} from "./tiled-plan-viewer.helpers";

/**
 * DZI tile source whose downloads read through the bounded opportunistic
 * tile cache (`src/offline/tile-cache.ts`): IDB hit → finish immediately;
 * miss OR cache-layer error (fail-open) → network fetch → finish +
 * fire-and-forget bounded cache write; offline miss → fail (OSD keeps
 * whatever lower-level tiles it has).
 * `downloadTileStart`/`finish(data, request, dataType)` verified against the
 * bundled OSD 6.0.2 — its own non-AJAX path finishes an HTMLImageElement
 * with dataType `"image"`.
 */
class CachedPlanTileSource extends OpenSeadragon.TileSource {
  // Plain field declarations + assignments (not constructor parameter
  // properties) — the repo compiles with `erasableSyntaxOnly`.
  private readonly meta: TilesMeta;
  private readonly revisionId: string;
  private readonly ownerProjectId: string;

  constructor(meta: TilesMeta, revisionId: string, ownerProjectId: string) {
    super({
      width: meta.width,
      height: meta.height,
      tileSize: meta.tile_size,
      tileOverlap: meta.overlap,
    });
    this.meta = meta;
    this.revisionId = revisionId;
    this.ownerProjectId = ownerProjectId;
  }

  getTileUrl(level: number, x: number, y: number): string {
    return tileUrlFor(this.meta)(level, x, y);
  }

  downloadTileStart(context: OpenSeadragon.ImageJob): void {
    const tile = context.tile;
    void (async () => {
      try {
        // The cache read FAILS OPEN: a rejected IDB lookup (blocked/broken
        // IndexedDB — getDB() rejections propagate) degrades to the network
        // fetch. Sharing the catch below would context.fail every tile and
        // gray the canvas while ONLINE, and open-failed never fires (the
        // source itself opens fine) so the FE-DEBT-007 fallback can't save it.
        let blob = await getCachedTile(
          this.revisionId,
          tile.level,
          tile.x,
          tile.y,
        ).catch(() => null);
        if (!blob) {
          const res = await fetch(this.getTileUrl(tile.level, tile.x, tile.y), {
            mode: "cors",
            credentials: "omit",
          });
          if (!res.ok) throw new Error(`tile HTTP ${res.status}`);
          blob = await res.blob();
          void cacheTile(
            this.revisionId,
            this.ownerProjectId,
            tile.level,
            tile.x,
            tile.y,
            blob,
          );
        }
        const img = new Image();
        const url = URL.createObjectURL(blob);
        img.onload = () => {
          URL.revokeObjectURL(url);
          context.finish(img, null, "image"); // dataType per OSD 6's own image path
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          context.fail("tile decode failed", null);
        };
        img.src = url;
      } catch (err) {
        context.fail(err instanceof Error ? err.message : "tile fetch failed", null);
      }
    })();
  }

  downloadTileAbort(): void {
    // Deliberate v1 scope cut — per-tile aborts ARE possible (an
    // AbortController per job, threaded into the fetch above), just not
    // wired yet: tiles are small (≤~40KB) and OSD drops stale results, so
    // the cost is wasted bytes and the "Ignoring tile" console flood on
    // teardown, not correctness. Tracked as FE-DEBT-008. Overridden anyway:
    // the base implementation reads `userData` fields our start() never
    // sets. (Param dropped — TS overrides may take fewer params; lint
    // forbids unused ones.)
  }
}

/**
 * What the host can ask of the surface without owning the OpenSeadragon
 * instance: frame a box, or bring a point back on screen. Both take normalized
 * `[0,1]` plan coordinates and are no-ops until the viewer has opened.
 * See [[tiled-plan-viewer.spec]] "Imperative handle".
 */
export interface TiledPlanViewerHandle {
  /** Animate to frame `bounds` (padded), within the zoom limits. */
  fitToBounds: (bounds: NormalizedBounds) => void;
  /** Pan — never zoom — so `point` is comfortably on screen; no-op if it is. */
  ensureVisible: (point: XY) => void;
  /** Pan — never zoom — so `point` is the middle of the visible area. */
  centerOn: (point: XY) => void;
}

/**
 * One pinned thing on the plan: id keys the OSD overlay, (x, y) is the
 * normalized [0,1] anchor, render() is portalled into a zero-size overlay
 * div centred on the anchor. The EXISTING pin components (PinMarkerDot,
 * PinDirectionCone, PinRotateHandle, the PlanViewer PinMarker/PinCluster)
 * render unchanged inside with xPct/yPct = 0 (or left/top 0%): the overlay
 * div IS the pin point, and their translate(-50%, -50%) self-centring does
 * the rest. See [[tiled-plan-viewer.spec]].
 */
export interface TiledOverlayItem {
  id: string;
  x: number;
  y: number;
  render: () => React.ReactNode;
}

export interface TiledPlanViewerProps {
  tiles: TilesMeta;
  /** Keys the viewer instance — switching plans tears down and rebuilds. */
  planId: string;
  /** Scopes the tile cache: keys tiles (`tile:{revisionId}:…`). */
  revisionId: string;
  /** Owner project — rides on cached tile rows so unpin clears them. */
  projectId: string;
  overlays?: TiledOverlayItem[];
  /** Quick tap (not a drag) on the sheet, in normalized [0,1] coords. */
  onTap?: (coords: XY) => void;
  /**
   * Zoom relative to home/fit — the same scale convention
   * react-zoom-pan-pinch reported (1 = fitted), so usePinClustering and the
   * `isZoomed` chip keep their semantics. Fired on `zoom`.
   */
  onZoomChange?: (relativeZoom: number) => void;
  /**
   * Cluster-commit signal (fires on `animation-finish`) plus the sheet's
   * CURRENT on-screen size at home zoom — the `imageDimensions` analogue
   * usePinClustering expects. Fired on open, resize, and animation end.
   */
  onViewSettled?: (info: {
    relativeZoom: number;
    fitWidth: number;
    fitHeight: number;
    /** The zoom ceiling, in the same relative-to-fit convention as `relativeZoom`. */
    maxRelativeZoom: number;
  }) => void;
  /** Crosshair cursor while placing a pin. */
  pinMode?: boolean;
  /**
   * OSD failed to open the tile source (e.g. a ready-status revision whose
   * tiles 404). Hosts respond by falling back to the legacy `<img>` surface
   * for the session — resolves FE-DEBT-007's permanent gray surface.
   */
  onOpenFailed?: () => void;
  /** Imperative framing handle for the host (the cluster panel's Zoom to area). */
  ref?: React.Ref<TiledPlanViewerHandle>;
  className?: string;
}

/** A view within this of fit (relative zoom 1) counts as "at fit". */
const FITTED_EPSILON = 0.02;

/** Match the legacy surfaces' tap discrimination (PlanViewer: <10px, <400ms). */
const TAP_DIST_THRESHOLD_PX = 10;
const TAP_TIME_THRESHOLD_MS = 400;

/**
 * Per-overlay host record. `el` is created during render (lazy init) so the
 * portal mounts content into it in the SAME commit; the reconciliation effect
 * owns the OSD side: `registered` flips on addOverlay, and (x, y) cache the
 * last coords applied to OSD so unchanged overlays skip the update call.
 */
interface OverlayHostRecord {
  el: HTMLDivElement;
  registered: boolean;
  x: number;
  y: number;
}

export function TiledPlanViewer({
  tiles,
  planId,
  revisionId,
  projectId,
  overlays = [],
  onTap,
  onZoomChange,
  onViewSettled,
  pinMode = false,
  onOpenFailed,
  ref,
  className,
}: TiledPlanViewerProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const viewerRef = React.useRef<OpenSeadragon.Viewer | null>(null);
  const [viewerReady, setViewerReady] = React.useState(false);
  // id → overlay host record (portal target div + what's been applied to OSD).
  const overlayHostsRef = React.useRef(new Map<string, OverlayHostRecord>());
  const callbacksRef = React.useRef({
    onTap,
    onZoomChange,
    onViewSettled,
    onOpenFailed,
  });
  callbacksRef.current = { onTap, onZoomChange, onViewSettled, onOpenFailed };

  // Host divs are get-or-created DURING render — idempotent lazy init keyed by
  // id — so createPortal mounts the pin content into the (detached) div in the
  // same commit. Creating them in the effect instead left the first render's
  // portals targetless: a ref mutation schedules no re-render, so pins stayed
  // invisible until something unrelated re-rendered (fatal on a static
  // ExpandedPlanView). The effect below still owns every OSD call.
  const getOrCreateHostEl = React.useCallback((id: string): HTMLDivElement => {
    const hosts = overlayHostsRef.current;
    let host = hosts.get(id);
    if (!host) {
      const el = document.createElement("div");
      el.dataset.overlayId = id;
      // Zero-size anchor: children use left/top 0% + translate(-50%,-50%).
      el.style.width = "0";
      el.style.height = "0";
      el.style.setProperty("--pin-scale", "1");
      // NaN coords = "nothing applied to OSD yet" (NaN never equals item.x).
      host = { el, registered: false, x: Number.NaN, y: Number.NaN };
      hosts.set(id, host);
    }
    return host.el;
  }, []);

  // ---- viewer lifecycle -------------------------------------------------
  React.useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    // The Map identity is stable for the component's life — capture it so the
    // cleanup clears the same instance it started with (and the lint rule
    // about ref reads in cleanups is satisfied).
    const overlayHosts = overlayHostsRef.current;

    const viewer = OpenSeadragon({
      element,
      // Custom source: same inline DZI geometry + locked URL shape, but
      // every tile download reads through the bounded IDB tile cache.
      tileSources: new CachedPlanTileSource(tiles, revisionId, projectId),
      // Zoom bounds: legacy RZPP allowed 0.5x of fit (minScale) — keep that;
      // the max bound deliberately changes meaning from 20x-of-fit to
      // native-pixel-relative (maxZoomPixelRatio 2 ≈ 200% of true size) —
      // deep zoom to dimension text is the whole feature.
      minZoomImageRatio: 0.5,
      maxZoomPixelRatio: 2,
      visibilityRatio: 1,
      constrainDuringPan: true,
      // Tap discrimination — same thresholds as the legacy pointer math.
      clickTimeThreshold: TAP_TIME_THRESHOLD_MS,
      clickDistThreshold: TAP_DIST_THRESHOLD_PX,
      gestureSettingsMouse: { clickToZoom: false, dblClickToZoom: true, flickEnabled: false },
      gestureSettingsTouch: { clickToZoom: false, dblClickToZoom: true, flickEnabled: false },
      showNavigator: false,
      showNavigationControl: false,
      crossOriginPolicy: "Anonymous",
      prefixUrl: "", // no OSD button images — chrome is ours
    });
    viewerRef.current = viewer;

    const emitSettled = () => {
      const home = viewer.viewport.getHomeZoom();
      const zoom = viewer.viewport.getZoom(true);
      const relativeZoom = home > 0 ? zoom / home : 1;
      // On-screen size of the whole sheet at home zoom — the analogue of the
      // legacy <img> rendered size that usePinClustering divides by.
      const tl = viewer.viewport.imageToViewerElementCoordinates(new OpenSeadragon.Point(0, 0));
      const br = viewer.viewport.imageToViewerElementCoordinates(
        new OpenSeadragon.Point(tiles.width, tiles.height),
      );
      const fitWidth = Math.abs(br.x - tl.x) / relativeZoom;
      const fitHeight = Math.abs(br.y - tl.y) / relativeZoom;
      const maxRelativeZoom =
        home > 0 ? viewer.viewport.getMaxZoom() / home : relativeZoom;
      callbacksRef.current.onViewSettled?.({
        relativeZoom,
        fitWidth,
        fitHeight,
        maxRelativeZoom,
      });
    };

    viewer.addHandler("open", () => {
      setViewerReady(true);
      emitSettled();
    });
    // A ready-status revision whose tiles are unreachable must not strand a
    // permanent gray surface — surface the failure so hosts can fall back to
    // the legacy <img> path (FE-DEBT-007).
    viewer.addHandler("open-failed", () => {
      callbacksRef.current.onOpenFailed?.();
    });
    // Zoom relative to home as of the last zoom event — read by the resize
    // handler below BEFORE OSD applies that resize's own zoom change.
    let lastRelativeZoom = 1;
    const reportZoom = () => {
      const home = viewer.viewport.getHomeZoom();
      const zoom = viewer.viewport.getZoom(true);
      lastRelativeZoom = home > 0 ? zoom / home : 1;
      callbacksRef.current.onZoomChange?.(lastRelativeZoom);
    };
    viewer.addHandler("zoom", reportZoom);
    // OSD raises `zoom` when a zoom is REQUESTED, while the zoom spring is still
    // at its old value — not as it settles. A programmatic zoom (the cluster
    // panel's Zoom to area) therefore reports its final zoom here, or the host's
    // "zoomed" state would depend on timing.
    viewer.addHandler("animation-finish", () => {
      reportZoom();
      emitSettled();
    });
    viewer.addHandler("resize", () => {
      // OSD rescales a resized container by the ratio of its DIAGONALS, so a
      // sheet that was fitted ends up smaller than the room it now has (a plan
      // shortened by the cluster sheet, narrowed by the cluster panel, …). A
      // view that was at fit stays at fit: re-fit it once OSD has finished
      // applying its own resize. A zoomed-in view keeps OSD's behaviour.
      // See [[tiled-plan-viewer.spec]] "Container resizes".
      // A zoom already under way (target ≠ current) is not a fitted view: its
      // destination is the point, and a re-fit would cancel it.
      const idle =
        Math.abs(viewer.viewport.getZoom(true) - viewer.viewport.getZoom(false)) <
        1e-9;
      const wasFitted = idle && Math.abs(lastRelativeZoom - 1) < FITTED_EPSILON;
      emitSettled();
      if (wasFitted) {
        setTimeout(() => {
          if (viewerRef.current !== viewer || !viewer.isOpen()) return;
          viewer.viewport.goHome(true);
          reportZoom();
          emitSettled();
        }, 0);
      }
    });

    // Tap-to-place: OSD's own tap/drag discrimination (`quick` honours the
    // click thresholds above) → pixel → image px → normalized [0,1] → the
    // same callback signature the legacy screenToImageCoords path fed.
    viewer.addHandler("canvas-click", (event) => {
      if (!event.quick) return;
      const imagePoint = viewer.viewport.viewerElementToImageCoordinates(event.position);
      const coords = imageToNormalized({ x: imagePoint.x, y: imagePoint.y }, tiles);
      if (coords) callbacksRef.current.onTap?.(coords);
    });

    return () => {
      overlayHosts.clear();
      setViewerReady(false);
      viewerRef.current = null;
      viewer.destroy();
    };
    // Tear down + rebuild per plan/tile-set — OSD has no cheap source swap
    // and plan switches also reset zoom in the legacy viewer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, tiles.base_url]);

  // ---- overlay reconciliation (OSD side only) ----------------------------
  // Point-located OSD overlays are NOT scaled by zoom — a 40/44px marker
  // keeps its size on screen at any zoom. That is exactly what the legacy
  // surfaces achieved with `--pin-scale: 1/scale` countering the container
  // scale, so the var is pinned to 1 here and no zoom listener resizes pins.
  React.useEffect(() => {
    const viewer = viewerRef.current;
    // isOpen() also covers the one stale-state commit of a plan switch: this
    // effect can run with last render's viewerReady=true while the REPLACEMENT
    // viewer hasn't opened — registering then would compute garbage pre-open
    // locations and poison the position cache below.
    if (!viewer || !viewerReady || !viewer.isOpen()) return;
    const hosts = overlayHostsRef.current;
    const seen = new Set<string>();

    for (const item of overlays) {
      seen.add(item.id);
      // Render created the host (render always precedes effects); if a torn-
      // down map raced us the next render/effect pass repairs it.
      const host = hosts.get(item.id);
      if (!host) continue;
      // Update-skip: same coords already applied → no OSD call (a parent
      // re-render otherwise costs one updateOverlay per pin on a dense sheet).
      if (host.registered && host.x === item.x && host.y === item.y) continue;
      const imagePx = normalizedToImage({ x: item.x, y: item.y }, tiles);
      const location = viewer.viewport.imageToViewportCoordinates(
        new OpenSeadragon.Point(imagePx.x, imagePx.y),
      );
      if (host.registered) {
        viewer.updateOverlay(host.el, location, OpenSeadragon.Placement.CENTER);
      } else {
        viewer.addOverlay({
          element: host.el,
          location,
          placement: OpenSeadragon.Placement.CENTER,
          checkResize: false,
        });
        host.registered = true;
      }
      host.x = item.x;
      host.y = item.y;
    }
    for (const [id, host] of hosts) {
      if (!seen.has(id)) {
        if (host.registered) viewer.removeOverlay(host.el);
        hosts.delete(id);
      }
    }
  }, [overlays, viewerReady, tiles]);

  // ---- imperative framing handle ------------------------------------------
  React.useImperativeHandle(
    ref,
    () => ({
      fitToBounds(bounds) {
        const viewer = viewerRef.current;
        if (!viewer || !viewer.isOpen()) return;
        const padded = paddedBounds(bounds, tiles.width, tiles.height);
        const from = normalizedToImage({ x: padded.minX, y: padded.minY }, tiles);
        const to = normalizedToImage({ x: padded.maxX, y: padded.maxY }, tiles);
        const rect = viewer.viewport.imageToViewportRectangle(
          from.x,
          from.y,
          to.x - from.x,
          to.y - from.y,
        );
        // The "WithConstraints" variant clamps the zoom to the bounds set at
        // construction, so a one-point box lands at the ceiling, not past it.
        viewer.viewport.fitBoundsWithConstraints(rect);
      },
      centerOn(point) {
        const viewer = viewerRef.current;
        if (!viewer || !viewer.isOpen()) return;
        const imagePx = normalizedToImage(point, tiles);
        viewer.viewport.panTo(
          viewer.viewport.imageToViewportCoordinates(
            new OpenSeadragon.Point(imagePx.x, imagePx.y),
          ),
        );
        viewer.viewport.applyConstraints();
      },
      ensureVisible(point) {
        const viewer = viewerRef.current;
        if (!viewer || !viewer.isOpen()) return;
        const imagePx = normalizedToImage(point, tiles);
        const target = viewer.viewport.imageToViewportCoordinates(
          new OpenSeadragon.Point(imagePx.x, imagePx.y),
        );
        const visible = viewer.viewport.getBoundsNoRotate();
        const inset = REVEAL_INSET_RATIO * Math.min(visible.width, visible.height);
        const comfortable =
          target.x >= visible.x + inset &&
          target.x <= visible.x + visible.width - inset &&
          target.y >= visible.y + inset &&
          target.y <= visible.y + visible.height - inset;
        if (comfortable) return;
        viewer.viewport.panTo(target);
        viewer.viewport.applyConstraints();
      },
    }),
    [tiles],
  );

  return (
    <div
      ref={containerRef}
      data-testid="tiled-plan-viewer"
      className={cn("h-full w-full bg-gray-100 dark:bg-card", pinMode && "cursor-crosshair", className)}
      style={{ touchAction: "none" }}
    >
      {viewerReady &&
        overlays.map((item) =>
          // Get-or-create is the render-time half of the overlay contract: the
          // portal target exists (detached) before OSD attaches it post-effect.
          createPortal(item.render(), getOrCreateHostEl(item.id), item.id),
        )}
    </div>
  );
}
